import { resolveWorkflowRouteSettings } from '@Core/lib/tasks/utils/workflow';
import { buildRouteUrl, extractRouteParams } from '@Core/utils/routeParams';
import { WorkflowRouteSettingsContext } from '@Pages/pageRuntime/context/WorkflowRouteSettingsContext';
import type { SdkWorkflowResponse } from '@Pages/pageRuntime/sdk/types';
import axios from 'axios';
import { useCallback, useContext } from 'react';

export function useWorkflowRequest(workflowId: string, params?: Record<string, unknown>) {
  const routeSettings = useContext(WorkflowRouteSettingsContext);
  const settings = workflowId
    ? (resolveWorkflowRouteSettings(routeSettings, workflowId)?.settings ?? null)
    : null;
  const method = (settings?.method ?? '').toUpperCase();
  const path = settings?.route ? buildRouteUrl(settings.route, params) : null;
  let workflowUrl = path;

  if (settings?.origin === null) {
    workflowUrl = null;
  } else if (path && settings?.origin) {
    workflowUrl = `${settings.origin}${path.startsWith('/') ? path : `/${path}`}`;
  }

  const requestMethod = settings?.method.toLowerCase() ?? null;
  // A URL still carrying `{param}` placeholders would 404 — `Sdk.utils.workflow.url`
  // throws for that case; the render-safe equivalent here is staying not-ready,
  // which also covers `params` values that arrive asynchronously.
  const isReady =
    Boolean(workflowUrl && settings?.method) && extractRouteParams(workflowUrl).length === 0;

  // Public hooks use `submit` as a callback dependency, so its identity must only change when the
  // resolved request changes rather than whenever the consuming hook re-renders.
  // react-doctor-disable-next-line react-compiler-no-manual-memoization
  const submit = useCallback(
    (inputs: Record<string, unknown>) => {
      if (!isReady || !workflowUrl || !requestMethod) {
        return Promise.reject(new Error('Workflow not ready for submission'));
      }

      return axios
        .request<SdkWorkflowResponse>({
          method: requestMethod,
          url: workflowUrl,
          ...(requestMethod === 'get' ? { params: inputs } : { data: inputs })
        })
        .then((response) => response.data);
    },
    [isReady, requestMethod, workflowUrl]
  );

  return { settings, method, isReady, submit, workflowUrl };
}
